import InfoIcon from "@mui/icons-material/Info";
import { Box, type BoxProps, Tooltip } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useContrastBorder } from "hooks/contrast-border";
import type { FC } from "react";
import { formatColorText } from "service/color-formatting";
import { labToCss } from "service/color-math";
import type { NamedColor } from "../filter/color-filter/ColorFilter";

export interface ColorPreviewDisplayProps extends BoxProps {
  preview: NamedColor;
  contrastBorder?: boolean;
  alertSize?: "small" | "medium";
}

export const ColorPreviewDisplay: FC<ColorPreviewDisplayProps> = ({
  preview,
  contrastBorder = false,
  alertSize = "small",
  ...boxProps
}) => {
  const label = `Result: ${formatColorText(preview.value)}`;
  const contrastBorderValue = useContrastBorder(preview.value);
  const contrastColor =
    preview.value[0] > 80 ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.8)";

  const alertSizeEm = {
    small: "1.25em",
    medium: "1.5em",
  }[alertSize];
  const alertSpacing = {
    small: 2,
    medium: 6,
  }[alertSize];

  return (
    <>
      <Box
        title={label}
        {...boxProps}
        bgcolor={labToCss(preview.value)}
        border={contrastBorder ? contrastBorderValue : undefined}
        position="relative"
      >
        {preview.warning && (
          <Box
            position="absolute"
            left={alertSpacing}
            bottom={alertSpacing}
            sx={{ lineHeight: 0 }}
          >
            <Tooltip title={preview.warning} arrow>
              <InfoIcon sx={{ color: contrastColor, fontSize: alertSizeEm }} />
            </Tooltip>
          </Box>
        )}
      </Box>
      <Box component="span" sx={visuallyHidden}>
        {label}
      </Box>
    </>
  );
};
